import { useMemo } from "react";
import { Flex, Stack } from "@/components/layout";
import { Button, EmptyState } from "@/components/ui";
import {
  AttentionList,
  BarList,
  KpiCard,
  KpiGrid,
  LoadBoundary,
  Panel,
  TwoCol,
  useHubNavigate,
  type AttentionEntry,
} from "@/modules/hub-kit/HubKit";
import { ageLabel, countWhere, formatCount, formatMinor, humanize } from "@/modules/hub-kit/format";
import { pesosToMinor, type CreatorsOverview } from "../overviewApi";

const OPEN = ["submitted", "under_review", "more_information_required", "on_hold"];
const PAY_OWED = ["eligible", "pending_review", "approved", "processing", "held"];
const PAY_DONE = ["paid", "settled"];

const TIER_ORDER = ["verified_creator", "partner_creator", "ambassador_creator"];

export function CreatorOverviewTab({
  data,
  error,
  loading,
  reload,
}: {
  data: CreatorsOverview | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}) {
  return (
    <Stack gap={16}>
      <Flex gap={8}>
        <Button variant="secondary" onClick={reload}>
          Refresh
        </Button>
      </Flex>
      <LoadBoundary
        loading={loading}
        error={error}
        onRetry={reload}
        hasData={!!data}
        what="the Creator Center overview"
      >
        {data ? <Body o={data} /> : null}
      </LoadBoundary>
    </Stack>
  );
}

function Body({ o }: { o: CreatorsOverview }) {
  const go = useHubNavigate();

  const openApps = o.applications.filter((a) => OPEN.includes(a.status));
  const openCount = openApps.reduce((n, a) => n + a.count, 0);
  const needsInfo = countWhere(o.applications, ["more_information_required"]);
  const oldest = openApps
    .filter((a) => a.status !== "more_information_required")
    .map((a) => a.oldest_submitted_at)
    .filter((x): x is string => !!x)
    .sort()[0];
  const active = o.profiles.filter((p) => p.status === "active_creator").reduce((n, p) => n + p.count, 0);
  const restricted = countWhere(o.profiles, ["restricted", "suspended"]);
  const approved30 = countWhere(o.decisions_last_30d, ["approved", "conditionally_approved"]);
  const rejected30 = countWhere(o.decisions_last_30d, ["rejected"]);

  const currency = o.payments[0]?.currency ?? "PHP";
  const owedMinor = o.payments
    .filter((p) => PAY_OWED.includes(p.status) && p.currency === currency)
    .reduce((n, p) => n + pesosToMinor(p.amount), 0);
  const paidMinor = o.payments
    .filter((p) => PAY_DONE.includes(p.status) && p.currency === currency)
    .reduce((n, p) => n + pesosToMinor(p.amount), 0);
  const heldOrFailed = countWhere(o.payments, ["held", "failed", "disputed"]);

  const nothing =
    o.applications.length === 0 && o.profiles.length === 0 && o.payments.length === 0 && o.programs.length === 0;

  const attention: AttentionEntry[] = [];
  if (openCount > 0) {
    attention.push({
      key: "open",
      severity: oldest && Date.now() - new Date(oldest).getTime() > 3 * 86400000 ? "warning" : "info",
      title: `${openCount} application${openCount === 1 ? "" : "s"} waiting for a decision`,
      detail: [
        o.unassigned_open_applications ? `${o.unassigned_open_applications} unassigned` : null,
        oldest ? `oldest waiting ${ageLabel(oldest)}` : null,
      ]
        .filter(Boolean)
        .join(", "),
      actionLabel: "Review",
      onAction: () => go("applications"),
    });
  }
  if (needsInfo > 0) {
    attention.push({
      key: "info",
      severity: "info",
      title: `${needsInfo} waiting on the applicant for more information`,
      detail: "Play has no resubmit step, so the applicant answers through Support.",
      actionLabel: "Open",
      onAction: () => go("applications"),
    });
  }
  if (heldOrFailed > 0) {
    attention.push({
      key: "pay",
      severity: "warning",
      title: `${heldOrFailed} creator payment${heldOrFailed === 1 ? "" : "s"} held, failed or disputed`,
      actionLabel: "Open payouts",
      onAction: () => go("payouts"),
    });
  }
  if (restricted > 0) {
    attention.push({
      key: "restricted",
      severity: "info",
      title: `${restricted} creator profile${restricted === 1 ? "" : "s"} restricted or suspended`,
      actionLabel: "Open creators",
      onAction: () => go("creators"),
    });
  }

  const tierRows = TIER_ORDER.map((tier) => {
    const n = o.profiles
      .filter((p) => p.tier === tier && p.status === "active_creator")
      .reduce((s, p) => s + p.count, 0);
    return { key: tier, label: humanize(tier), value: n, display: formatCount(n) };
  }).filter((r) => r.value > 0);

  const pipelineRows = [
    "submitted",
    "under_review",
    "more_information_required",
    "on_hold",
    "approved",
    "conditionally_approved",
    "rejected",
  ]
    .map((st) => ({ st, n: countWhere(o.applications, [st]) }))
    .filter((r) => r.n > 0)
    .map((r) => ({
      key: r.st,
      label: humanize(r.st),
      value: r.n,
      display: formatCount(r.n),
      tone: (r.st === "rejected"
        ? "danger"
        : r.st.startsWith("approved") || r.st === "conditionally_approved"
          ? "success"
          : "warning") as "danger" | "success" | "warning",
    }));

  const serviceRows = o.freelance_services.map((s) => ({
    key: s.service,
    label: s.service,
    value: s.count,
    display: formatCount(s.count),
  }));

  return (
    <Stack gap={24}>
      {nothing ? (
        <EmptyState
          title="No creators yet"
          description="Creators appear here when a player applies for a creator tier from Play (Profile, then Get Verified). Their application lands in Applications for review."
          action={
            <Button variant="secondary" onClick={() => go("requirements")}>
              See what applicants are asked
            </Button>
          }
        />
      ) : null}
      <KpiGrid>
        <KpiCard
          label="Awaiting decision"
          value={formatCount(openCount)}
          hint={oldest ? `Oldest ${ageLabel(oldest)}` : "Nothing waiting"}
          {...(openCount > 0 ? { tone: "warning" as const } : {})}
          onClick={() => go("applications")}
        />
        <KpiCard
          label="Active creators"
          value={formatCount(active)}
          hint={restricted ? `${restricted} restricted or suspended` : "Approved and active"}
          onClick={() => go("creators")}
        />
        <KpiCard
          label="Decided (30 days)"
          value={formatCount(approved30 + rejected30)}
          hint={`${approved30} approved · ${rejected30} rejected`}
        />
        <KpiCard
          label="Owed to creators"
          value={formatMinor(owedMinor, currency)}
          hint="Eligible through processing"
          onClick={() => go("payouts")}
        />
        <KpiCard label="Paid to creators" value={formatMinor(paidMinor, currency)} hint="Paid or settled" />
      </KpiGrid>

      <Panel title="Needs attention">
        <AttentionList entries={attention} clearMessage="Nothing waiting: no open applications and no stuck creator payments." />
      </Panel>

      <TwoCol>
        <Panel title="Application pipeline" description="All applications by status.">
          <BarList rows={pipelineRows} emptyMessage="No applications submitted yet." />
        </Panel>
        <Panel title="Active creators by tier">
          <BarList rows={tierRows} emptyMessage="No active creators yet." />
        </Panel>
        <Panel
          title="Freelance services offered"
          description="What coaches, trainers and instructors said they offer on their HUDDLr registration (freelance_services)."
        >
          <BarList rows={serviceRows} emptyMessage="No freelance registrations have listed services yet." />
        </Panel>
        <Panel title="Programs and campaigns">
          <BarList
            rows={[
              { key: "programs", label: "Programs", value: o.programs.reduce((n, p) => n + p.count, 0) },
              { key: "memberships", label: "Memberships", value: o.memberships.reduce((n, p) => n + p.count, 0) },
              { key: "campaigns", label: "Campaigns", value: o.campaigns.reduce((n, p) => n + p.count, 0) },
              { key: "invitations", label: "Invitations", value: o.invitations.reduce((n, p) => n + p.count, 0) },
              { key: "participations", label: "Participations", value: o.participations.reduce((n, p) => n + p.count, 0) },
            ]
              .filter((r) => r.value > 0)
              .map((r) => ({ ...r, display: formatCount(r.value) }))}
            emptyMessage="No programs or campaigns yet. Create a program under Programs & campaigns."
          />
        </Panel>
      </TwoCol>
    </Stack>
  );
}

export function useCreatorBadges(o: CreatorsOverview | null) {
  return useMemo(() => {
    if (!o) return { open: 0, payIssues: 0 };
    return {
      open: countWhere(o.applications, ["submitted", "under_review"]),
      payIssues: countWhere(o.payments, ["held", "failed", "disputed"]),
    };
  }, [o]);
}
